/**
 * Modal de personalizacion del partido local.
 *
 * El menu de partido se quedo con UNA decision: el modo. Todo lo demas —
 * aspecto, jugador, ayuda y plan de equipo— vive aqui, porque son ajustes que
 * se tocan una vez y se olvidan, mientras que el modo se elige cada partida.
 *
 * Todo se guarda en `localStorage` y sobrevive entre sesiones: la gracia de
 * personalizar es no tener que repetirlo.
 */
import {
  applyTeamSetup,
  ASSIST_LEVELS,
  AUTO_AWAY_KIT,
  awayKitOf,
  CUSTOM_KIT_ID,
  customKit,
  DEFAULT_ASSIST,
  DEFAULT_TEAM_SETUP,
  homeKitOf,
  KIT_IDS,
  KIT_PATTERNS,
  KITS,
  loadStoredPitchSkin,
  loadStoredTeamSetup,
  PITCH_SKIN_IDS,
  PITCH_SKINS,
  saveStoredPitchSkin,
  saveStoredTeamSetup,
  TEAM_INSTRUCTIONS,
  type AssistLevel,
  type Kit,
  type KitPattern,
  type PitchSkinId,
  type TeamSetup,
  MATCH_DEFAULT_SECONDS,
  MATCH_MINUTES_MAX,
  MATCH_MINUTES_MIN,
  setMatchFormat,
  isDrawRule,
  MATCH_PERIODS_MAX,
  MATCH_PERIODS_MIN,
  type MatchDrawRule,
} from "@golazo/shared";
import "./customize.css";
import { REWARD_KITS } from "../career/kits.js";
import { enforceUnlocks, unlockStore } from "../career/unlocks.js";
import { editNickname, loadNickname, onNicknameChange } from "../online/nickname.js";
import { t } from "../i18n/index.js";
import { COMPETITIVE_KITS } from "@golazo/shared";
import { tierLabel } from "./tierMark.js";

const SHIRT_STORAGE_KEY = "golazo.shirtNumber";
const ASSIST_STORAGE_KEY = "golazo.assist";
const MINUTES_STORAGE_KEY = "golazo.minutes";
const PERIODS_STORAGE_KEY = "golazo.periods";
const DRAW_STORAGE_KEY = "golazo.onDraw";
const PLAN_STORAGE_KEY = "golazo.teamPlan";
const DEFAULT_SHIRT = 10;

/**
 * Plan de equipo. Son las mismas palancas que usa la IA para colocarse
 * (`TEAM_INSTRUCTIONS`), expuestas al jugador: eliges CÓMO juega tu equipo,
 * no solo con qué camiseta.
 */
export interface TeamPlan {
  /** −1 replegado … +1 volcado arriba. */
  mentality: number;
  /** Intensidad del pressing coordinado. */
  pressing: number;
  /** Amplitud del bloque en ataque. */
  width: number;
  /** Ritmo: pase corto y rapido vs conduccion. */
  tempo: number;
  /**
   * Cuanto se pide a medios y carrilleros que lleguen desde atras (0..1).
   *
   * Tiene coste: el que llega deja un hueco. Por eso es una instruccion y no un
   * automatismo — un plan de "cerrojo" quiere a todo el mundo en su sitio.
   */
  lateRuns: number;
}

export interface CustomizeSettings {
  shirtNumber: number;
  nickname: string;
  assist: AssistLevel;
  /** Duracion elegida, en minutos. */
  minutes: number;
  periods: number;
  onDraw: MatchDrawRule;
  pitchSkin: PitchSkinId;
  plan: TeamPlan;
  /** Nombres y equipaciones de los dos equipos. */
  team: TeamSetup;
}

export interface CustomizeHandle {
  /** Abre el modal. Resuelve al cerrarlo, con los ajustes ya guardados. */
  open(): Promise<CustomizeSettings>;
  current(): CustomizeSettings;
  dispose(): void;
}

/** Preajustes de plan con nombre, para no obligar a tocar cuatro deslizadores. */
const PLAN_PRESETS: ReadonlyArray<{
  id: string;
  label: string;
  blurb: string;
  plan: TeamPlan;
}> = [
  {
    id: "control",
    label: "Control",
    blurb: t("Pase corto, bloque medio y paciencia. El balon es tuyo."),
    plan: { mentality: 0.2, pressing: 0.66, width: 0.72, tempo: 0.78, lateRuns: 0.6 },
  },
  {
    id: "vertical",
    label: "Vertical",
    blurb: t("En cuanto la recuperas, va para arriba. Menos toque, mas riesgo."),
    plan: { mentality: 0.55, pressing: 0.72, width: 0.66, tempo: 0.42, lateRuns: 0.9 },
  },
  {
    id: "asfixia",
    label: "Asfixia",
    blurb: t("Presion alta y campo ancho. Les quitas el aire en su area."),
    plan: { mentality: 0.45, pressing: 0.95, width: 0.9, tempo: 0.7, lateRuns: 0.75 },
  },
  {
    id: "cerrojo",
    label: "Cerrojo",
    blurb: t("Bloque bajo y salida a la contra. Que vengan ellos."),
    // Cerrojo: nadie abandona su sitio. Es el plan que renuncia a las llegadas.
    plan: { mentality: -0.35, pressing: 0.35, width: 0.45, tempo: 0.5, lateRuns: 0.15 },
  },
];

const DEFAULT_PLAN: TeamPlan = PLAN_PRESETS[0]!.plan;

function clampShirt(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_SHIRT;
  return Math.min(99, Math.max(1, Math.round(n)));
}

function loadShirt(): number {
  try {
    const raw = localStorage.getItem(SHIRT_STORAGE_KEY);
    return raw === null ? DEFAULT_SHIRT : clampShirt(Number(raw));
  } catch {
    return DEFAULT_SHIRT;
  }
}

function loadAssist(): AssistLevel {
  try {
    const raw = localStorage.getItem(ASSIST_STORAGE_KEY);
    return (ASSIST_LEVELS as readonly string[]).includes(raw ?? "")
      ? (raw as AssistLevel)
      : DEFAULT_ASSIST;
  } catch {
    return DEFAULT_ASSIST;
  }
}

function loadPlan(): TeamPlan {
  try {
    const raw = localStorage.getItem(PLAN_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PLAN };
    const parsed = JSON.parse(raw) as Partial<TeamPlan>;
    return {
      mentality: clamp01Signed(parsed.mentality ?? DEFAULT_PLAN.mentality),
      pressing: clamp01(parsed.pressing ?? DEFAULT_PLAN.pressing),
      width: clamp01(parsed.width ?? DEFAULT_PLAN.width),
      lateRuns: clamp01(parsed.lateRuns ?? DEFAULT_PLAN.lateRuns),
      tempo: clamp01(parsed.tempo ?? DEFAULT_PLAN.tempo),
    };
  } catch {
    return { ...DEFAULT_PLAN };
  }
}

function clamp01(v: number): number {
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.5;
}

function clamp01Signed(v: number): number {
  return Number.isFinite(v) ? Math.min(1, Math.max(-1, v)) : 0;
}

function save(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Modo privado o almacenamiento lleno: la partida sigue, sin recordar.
  }
}

function pitchHex(n: number): string {
  return `#${n.toString(16).padStart(6, "0")}`;
}

function pitchPreviewStyle(id: PitchSkinId): string {
  const skin = PITCH_SKINS[id];
  return [
    `--pitch-a:${pitchHex(skin.stripeA)}`,
    `--pitch-b:${pitchHex(skin.stripeB)}`,
    `--pitch-line:${pitchHex(skin.line)}`,
  ].join(";");
}

function hex(n: number): string {
  return `#${n.toString(16).padStart(6, "0")}`;
}

function fromHex(value: string): number {
  const parsed = Number.parseInt(value.replace("#", ""), 16);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Variables CSS de una camiseta, para que la miniatura se pinte sola.
 *
 * La muestra se dibuja con gradientes en CSS y no con un canvas: son 16
 * miniaturas por dos rejillas y ninguna tiene que animarse, asi que meter Pixi
 * aqui seria pagar un render entero por unas rayas.
 */
function kitPreviewStyle(kit: Kit): string {
  return [
    `--kit-a:${hex(kit.primary)}`,
    `--kit-b:${hex(kit.secondary)}`,
    `--kit-c:${hex(kit.accent)}`,
    `--kit-ink:${hex(kit.ink)}`,
  ].join(";");
}

const PATTERN_LABELS: Record<KitPattern, string> = {
  plain: "Lisa",
  stripes: "Rayas",
  halves: "Mitades",
  sash: "Banda",
  hoops: "Aros",
  quarters: "Cuartos",
  chevron: "Galón",
  band: "Franja",
  cross: "Cruz",
};

/**
 * Aplica el plan a las instrucciones del equipo local.
 *
 * `TEAM_INSTRUCTIONS` es un objeto vivo que la capa tactica lee cada tick, asi
 * que se muta en sitio en vez de reemplazarlo. Solo se toca `home`: el rival
 * mantiene su propio caracter.
 */
function loadMinutes(): number {
  let raw = NaN;
  try {
    // `Number(null)` es 0, no NaN: sin este `?? ""` la primera partida de
    // cualquiera salia con la duracion MINIMA en vez de con la de siempre.
    raw = Number(localStorage.getItem(MINUTES_STORAGE_KEY) ?? "");
  } catch {
    // Sin almacenamiento se juega con la duracion de siempre.
  }
  if (!Number.isFinite(raw) || raw <= 0) return Math.round(MATCH_DEFAULT_SECONDS / 60);
  return Math.min(MATCH_MINUTES_MAX, Math.max(MATCH_MINUTES_MIN, Math.round(raw)));
}

/** Lee un entero guardado, con tope. `Number(null)` es 0, de ahi el `?? ""`. */
function loadNum(key: string, fallback: number, min: number, max: number): number {
  let raw = NaN;
  try {
    raw = Number(localStorage.getItem(key) ?? "");
  } catch {
    // Sin almacenamiento se juega con lo de siempre.
  }
  if (!Number.isFinite(raw) || raw <= 0) return fallback;
  return Math.min(max, Math.max(min, Math.round(raw)));
}

function loadDraw(): MatchDrawRule {
  try {
    const raw = localStorage.getItem(DRAW_STORAGE_KEY);
    if (isDrawRule(raw)) return raw;
  } catch {
    // ignore
  }
  return "golden";
}

export function applyTeamPlan(plan: TeamPlan): void {
  TEAM_INSTRUCTIONS.home.mentality = plan.mentality;
  TEAM_INSTRUCTIONS.home.pressing = plan.pressing;
  TEAM_INSTRUCTIONS.home.width = plan.width;
  TEAM_INSTRUCTIONS.home.tempo = plan.tempo;
  TEAM_INSTRUCTIONS.home.lateRuns = plan.lateRuns;
}

export function createCustomize(root: HTMLElement = document.body): CustomizeHandle {
  const overlay = document.createElement("div");
  overlay.className = "customize-overlay is-hidden";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", t("Personalizar partido"));
  overlay.innerHTML = `
    <div class="customize-scrim" data-close></div>

    <div class="customize-panel">
      <header class="customize-head">
        <div>
          <p class="customize-kicker">${t("Partido local")}</p>
          <h2 class="customize-title">${t("Personalizar")}</h2>
        </div>
        <button type="button" class="customize-close" data-close aria-label="${t("Cerrar")}">✕</button>
      </header>

      <nav class="customize-tabs" role="tablist" aria-label="${t("Secciones")}">
        <button type="button" class="customize-tab is-active" role="tab" data-tab="jugador">${t("Jugador")}</button>
        <button type="button" class="customize-tab" role="tab" data-tab="equipo">${t("Equipo")}</button>
        <button type="button" class="customize-tab" role="tab" data-tab="cancha">${t("Cancha")}</button>
        <button type="button" class="customize-tab" role="tab" data-tab="reglas">${t("Reglas")}</button>
        <button type="button" class="customize-tab" role="tab" data-tab="plan">${t("Plan")}</button>
      </nav>

      <div class="customize-body">
        <section class="customize-pane is-active" data-pane="jugador">
          <div class="customize-row">
            <div class="customize-field">
              <h3 class="customize-label">${t("Dorsal")}</h3>
              <div class="shirt-compact">
                <button type="button" class="shirt-step" data-dir="-1" aria-label="${t("Bajar dorsal")}">−</button>
                <label class="shirt-display">
                  <span class="shirt-hash">#</span>
                  <input type="number" class="shirt-input" min="1" max="99" inputmode="numeric" aria-label="${t("Numero de dorsal")}" />
                </label>
                <button type="button" class="shirt-step" data-dir="1" aria-label="${t("Subir dorsal")}">+</button>
              </div>
              <!--
                La FICHA, junto a los numeros.
                El "#10" de la caja es un dato; esto es lo que vas a ver en el
                campo, con tu equipacion y tu dorsal encima. Sin ella hay que
                entrar a jugar para saber si el numero se lee sobre tu color.
              -->
              <span class="shirt-token kit-token" data-shirt-token aria-hidden="true">
                <span class="kit-token-num" data-shirt-token-num></span>
              </span>
            </div>

            <div class="customize-field">
              <h3 class="customize-label">${t("Apodo")}</h3>
              <!--
                Un boton, no un campo: el apodo es UNICO y lo confirma el
                servidor, asi que se cambia en un dialogo que dice si esta libre
                mientras escribes. Un campo que guardaba cada tecla dejaba
                creer que "Ana" era tuyo cuando era de otra persona.
              -->
              <button type="button" class="nick-input nick-change" data-nick-change aria-label="${t("Cambiar tu apodo")}">
                <span class="nick-change-name" data-nick-name></span>
                <span class="nick-change-cta">${t("Cambiar")} ✎</span>
              </button>
              <p class="customize-hint">${t("Tu nombre en las salas, en el chat y sobre tu jugador. Es único: nadie más puede llamarse igual.")}</p>
            </div>
          </div>
        </section>

        <section class="customize-pane" data-pane="equipo">
          <!--
            El escaparate. Ver las dos camisetas juntas, con tu dorsal y los dos
            nombres, es LO que se viene a hacer a esta pestaña: comprobar que
            saldras al campo como los tuyos. Antes habia que imaginarselo a
            partir de dos cuadraditos de color.
          -->
          <div class="kit-stage">
            <div class="kit-stage-team">
              <span class="kit-jersey kit-token" data-jersey="home"><span class="kit-token-num kit-jersey-num"></span></span>
              <input type="text" class="team-name-input" data-team="home" maxlength="16"
                     placeholder="${t("LOCAL")}" aria-label="${t("Nombre de tu equipo")}" />
              <span class="kit-stage-kit" data-stage-kit="home"></span>
            </div>
            <span class="kit-stage-vs">${t("vs")}</span>
            <div class="kit-stage-team">
              <span class="kit-jersey kit-token" data-jersey="away"><span class="kit-token-num kit-jersey-num"></span></span>
              <input type="text" class="team-name-input" data-team="away" maxlength="16"
                     placeholder="${t("VISITANTE")}" aria-label="${t("Nombre del rival")}" />
              <span class="kit-stage-kit" data-stage-kit="away"></span>
            </div>
          </div>

          <!--
            Una sola rejilla y un interruptor de bando. Con dos rejillas de
            dieciseis camisetas apiladas habia que hacer scroll a ciegas para
            llegar a la del rival, y las dos se veian iguales.
          -->
          <div class="kit-sides" role="tablist" aria-label="${t("Qué equipación estás eligiendo")}">
            <button type="button" class="kit-side is-active" role="tab" data-side="home">${t("LOCAL")}</button>
            <button type="button" class="kit-side" role="tab" data-side="away">${t("VISITANTE")}</button>
          </div>

          <p class="kit-count" data-kit-count></p>
          <div class="kit-grid" data-kit-grid role="radiogroup" aria-label="${t("Equipaciones")}"></div>

          <div class="kit-custom" data-kit-custom hidden>
            <div class="kit-colors">
              <label class="kit-color"><span>${t("Principal")}</span><input type="color" data-kit-color="primary" /></label>
              <label class="kit-color"><span>${t("Segundo")}</span><input type="color" data-kit-color="secondary" /></label>
              <label class="kit-color"><span>${t("Detalle")}</span><input type="color" data-kit-color="accent" /></label>
            </div>
            <div class="kit-patterns" data-kit-patterns role="radiogroup" aria-label="${t("Patrón de la camiseta")}"></div>
          </div>

          <p class="customize-hint" data-kit-blurb></p>
        </section>

        <section class="customize-pane" data-pane="cancha">
          <h3 class="customize-label">${t("Césped")}</h3>
          <div class="pitch-strip pitch-strip--grid" role="radiogroup" aria-label="${t("Estilo de cesped")}"></div>
          <p class="customize-hint" data-pitch-blurb></p>
        </section>

        <!--
          REGLAS, no "plan".
          Estaba al final de la pestaña de plan y habia que hacer scroll para
          encontrarlo: existia y no se veia, que para el jugador es lo mismo que
          no existir. Ademas son cosas distintas — el plan es COMO juegan tus
          companeros; esto es cuanto dura el partido y como se gana.
        -->
        <section class="customize-pane" data-pane="reglas">
          <h3 class="customize-label">${t("Duración del partido")}</h3>
          <div class="minutes-row">
            <button type="button" class="shirt-step" data-min-dir="-1" aria-label="${t("Menos minutos")}">−</button>
            <label class="minutes-display">
              <input type="number" class="minutes-input" min="1" max="30" inputmode="numeric"
                     aria-label="${t("Minutos de partido")}" />
              <span class="minutes-unit">${t("min")}</span>
            </label>
            <button type="button" class="shirt-step" data-min-dir="1" aria-label="${t("Más minutos")}">+</button>
          </div>
          <div class="minutes-presets" role="group" aria-label="${t("Duraciones rápidas")}">
            <button type="button" data-min="2">2</button>
            <button type="button" data-min="5">5</button>
            <button type="button" data-min="8">8</button>
            <button type="button" data-min="12">12</button>
            <button type="button" data-min="20">20</button>
          </div>

          <h3 class="customize-label">${t("Tiempos")}</h3>
          <div class="minutes-presets" role="group" aria-label="${t("Número de tiempos")}">
            <button type="button" data-periods="1">1</button>
            <button type="button" data-periods="2">2</button>
            <button type="button" data-periods="3">3</button>
            <button type="button" data-periods="4">4</button>
          </div>
          <p class="customize-hint" data-total-hint></p>

          <h3 class="customize-label">${t("Si acaban empatados")}</h3>
          <div class="minutes-presets" role="group" aria-label="${t("Si acaban empatados")}">
            <button type="button" data-draw="golden">${t("Gol de oro")}</button>
            <button type="button" data-draw="draw">${t("Empate")}</button>
          </div>
                </section>

        <section class="customize-pane" data-pane="plan">
          <h3 class="customize-label">${t("Plan de equipo")}</h3>
          <p class="customize-hint">${t("Como juegan tus companeros. Son las mismas palancas que usa la IA.")}</p>
          <div class="plan-presets" role="radiogroup" aria-label="${t("Preajustes de plan")}"></div>
          <p class="customize-hint" data-plan-blurb></p>
          <div class="plan-sliders"></div>

          <!--
            DURACION. Libre de verdad: de un minuto a media hora.
            No hay preajustes cerrados porque no hay una duracion correcta —
            un 2v2 rapido no dura lo que un 11v11 con amigos.
          -->
</section>

      </div>

      <footer class="customize-foot">
        <button type="button" class="customize-reset" data-reset>${t("Restablecer")}</button>
        <button type="button" class="customize-done" data-close>${t("Listo")}</button>
      </footer>
    </div>
  `;
  root.appendChild(overlay);

  let shirtNumber = loadShirt();
  let nickname = loadNickname();
  let assist = loadAssist();
  let minutes = loadMinutes();
  let periods = loadNum(PERIODS_STORAGE_KEY, 1, MATCH_PERIODS_MIN, MATCH_PERIODS_MAX);
  let onDraw: MatchDrawRule = loadDraw();
  let pitchSkin = loadStoredPitchSkin();
  let plan = loadPlan();
  /*
   * Solo se viste lo que se ha ganado (2026-09-26): de salida el rojo y el
   * azul, y el resto en la carrera. Un ajuste guardado con una camiseta que ya
   * no tienes vuelve al rojo (tu equipo) o al azul (el rival).
   */
  const unlocks = unlockStore();
  let team = enforceUnlocks(loadStoredTeamSetup(), unlocks.get());
  saveStoredTeamSetup(team);
  let resolveOpen: ((s: CustomizeSettings) => void) | null = null;

  const q = <T extends HTMLElement>(sel: string): T => overlay.querySelector(sel) as T;
  const shirtInput = q<HTMLInputElement>(".shirt-input");
  const nickName = q<HTMLElement>("[data-nick-name]");
  const showNick = (): void => {
    nickName.textContent = nickname ? `@${nickname}` : t("Sin nombre");
  };
  const pitchStrip = q(".pitch-strip");
  const pitchBlurb = q("[data-pitch-blurb]");
  const planPresets = q(".plan-presets");
  const planBlurb = q("[data-plan-blurb]");
  const planSliders = q(".plan-sliders");
  const kitCustom = q<HTMLElement>("[data-kit-custom]");
  const kitPatterns = q("[data-kit-patterns]");
  const kitBlurb = q("[data-kit-blurb]");
  const kitGrid = q<HTMLElement>("[data-kit-grid]");
  const kitCount = q<HTMLElement>("[data-kit-count]");
  /** Bando cuya equipacion se esta eligiendo ahora mismo. */
  let kitSide: "home" | "away" = "home";

  // ---------------------------------------------------------------- jugador

  const syncShirt = (): void => {
    shirtInput.value = String(shirtNumber);
    // El escaparate de equipaciones lleva tu dorsal puesto, y las fichas de la
    // rejilla tambien: si no, eliges una equipacion con un numero que no es el
    // tuyo.
    syncJerseys();
    if (kitGrid.childElementCount > 0) buildKitGrid();
    syncShirtToken();
  };

  const setShirt = (n: number): void => {
    shirtNumber = clampShirt(n);
    save(SHIRT_STORAGE_KEY, String(shirtNumber));
    syncShirt();
  };

  shirtInput.addEventListener("change", () => setShirt(Number(shirtInput.value) || DEFAULT_SHIRT));
  for (const btn of overlay.querySelectorAll<HTMLButtonElement>(".shirt-step")) {
    btn.addEventListener("click", () => setShirt(shirtNumber + (Number(btn.dataset.dir) || 0)));
  }

  q<HTMLButtonElement>("[data-nick-change]").addEventListener("click", () => {
    void editNickname();
  });
  // Lo que se gane en la carrera aparece aqui en el acto, sin reabrir nada.
  const unsubUnlocks = unlocks.subscribe(() => {
    team = enforceUnlocks(team, unlocks.get());
    commitTeam();
  });
  const unsubNick = onNicknameChange((next) => {
    nickname = next;
    showNick();
  });

  // ------------------------------------------------------------------ equipo

  /**
   * Las opciones del bando activo.
   *
   * "A medida" solo existe para TU equipo: es tu camiseta la que quieres
   * clavar. El rival, en cambio, estrena "Automática" y la trae puesta —
   * porque la unica regla innegociable aqui es que las dos se distingan, y eso
   * no puede depender de que nadie se acuerde de mirarlo.
   */
  const kitOptions = (
    side: "home" | "away",
  ): ReadonlyArray<{ id: string; kit: Kit; label: string; locked: boolean }> => {
    const owned = (id: string): boolean =>
      id === AUTO_AWAY_KIT || unlocks.has(id) || (id === CUSTOM_KIT_ID && unlocks.get().custom);
    const list = KIT_IDS.map((id) => ({ id, kit: KITS[id]!, label: t(KITS[id]!.label), locked: !owned(id) }));
    const all =
      side === "home"
        ? [...list, { id: CUSTOM_KIT_ID, kit: customKit(team.custom), label: t("A medida"), locked: !owned(CUSTOM_KIT_ID) }]
        : [
            {
              id: AUTO_AWAY_KIT,
              kit: awayKitOf({ ...team, awayKitId: AUTO_AWAY_KIT }),
              label: t("Automática"),
              locked: false,
            },
            ...list,
          ];
    // Primero lo que tienes: lo que se viene a hacer es elegir, y lo que se
    // puede elegir va delante. Lo bloqueado detras, a la vista, como meta.
    return [...all.filter((o) => !o.locked), ...all.filter((o) => o.locked)];
  };

  /** Como se gana una camiseta que aun no tienes. */
  const lockHint = (id: string): string => {
    if (id === CUSTOM_KIT_ID) return t("Se gana en la carrera: llega a Primera y diseña la tuya.");
    const reward = REWARD_KITS.find((r) => r.kitId === id);
    if (reward) return t("Se gana en la carrera: {how}", { how: t(reward.how) });
    const competitive = COMPETITIVE_KITS.find((k) => k.kitId === id);
    if (competitive?.champion) return t("Se gana compitiendo online: acaba una temporada mundial en el número 1.");
    if (competitive?.tier) {
      return t("Se gana compitiendo online: acaba una temporada mundial en {tier} o más.", { tier: tierLabel(competitive.tier) });
    }
    return t("La viste un club de la carrera. Acaba una temporada con ellos, o cámbiatela con su estrella siendo el MVP.");
  };

  const buildKitGrid = (): void => {
    kitGrid.replaceChildren();
    const options = kitOptions(kitSide);
    const ownedCount = options.filter((o) => !o.locked && o.id !== AUTO_AWAY_KIT).length;
    const total = options.filter((o) => o.id !== AUTO_AWAY_KIT).length;
    kitCount.textContent = t("Tienes {a} de {n}. El resto se gana en la carrera y compitiendo online.", { a: ownedCount, n: total });
    for (const option of options) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = option.locked ? "kit-card is-locked" : "kit-card";
      btn.dataset.kit = option.id;
      btn.setAttribute("role", "radio");
      if (option.locked) {
        btn.setAttribute("aria-disabled", "true");
        btn.title = lockHint(option.id);
      }
      btn.style.cssText = kitPreviewStyle(option.kit);
      /*
       * El boton es el JUGADOR, no una camiseta.
       *
       * Antes cada opcion era una silueta de camiseta recortada con
       * `clip-path`. Se veia bonita y no servia: en el campo no hay camisetas,
       * hay fichas octogonales con un dorsal dentro, y elegir mirando una cosa
       * para ver otra obliga a entrar al partido para saber como queda. Ahora
       * el boton ES la ficha, con tu dorsal puesto.
       */
      btn.innerHTML = `
        <span class="kit-token kit-token--${option.kit.pattern}" aria-hidden="true">
          <span class="kit-token-num">${shirtNumber}</span>
        </span>
        <span class="kit-card-label">${option.label}</span>
        ${option.locked ? `<span class="kit-lock" aria-label="${t("Bloqueada")}">🔒</span>` : ""}
      `;
      btn.addEventListener("click", () => {
        if (option.locked) {
          // No se elige: se cuenta como se gana, que es lo que se quiere saber.
          kitBlurb.textContent = `🔒 ${t(option.label)} · ${lockHint(option.id)}`;
          for (const other of kitGrid.querySelectorAll(".kit-card.is-hinted")) other.classList.remove("is-hinted");
          btn.classList.add("is-hinted");
          return;
        }
        team = kitSide === "home"
          ? { ...team, homeKitId: option.id }
          : { ...team, awayKitId: option.id };
        commitTeam();
      });
      kitGrid.appendChild(btn);
    }
  };

  for (const tab of overlay.querySelectorAll<HTMLElement>(".kit-side")) {
    tab.addEventListener("click", () => {
      kitSide = tab.dataset.side === "away" ? "away" : "home";
      commitTeam();
    });
  }

  for (const pattern of KIT_PATTERNS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "kit-pattern-pill";
    btn.dataset.pattern = pattern;
    btn.setAttribute("role", "radio");
    btn.textContent = t(PATTERN_LABELS[pattern]);
    btn.addEventListener("click", () => {
      // "A medida" tambien se gana: sin ella, ni colores ni cortes propios.
      if (!unlocks.get().custom) return;
      team = { ...team, custom: { ...team.custom, pattern }, homeKitId: CUSTOM_KIT_ID };
      commitTeam();
    });
    kitPatterns.appendChild(btn);
  }

  for (const input of overlay.querySelectorAll<HTMLInputElement>("[data-kit-color]")) {
    const key = input.dataset.kitColor as "primary" | "secondary" | "accent";
    input.addEventListener("input", () => {
      if (!unlocks.get().custom) return;
      team = {
        ...team,
        // Tocar un color ES elegir "A medida": obligar a seleccionar antes la
        // tarjeta hacia que los tres selectores no hicieran nada visible.
        homeKitId: CUSTOM_KIT_ID,
        custom: { ...team.custom, [key]: fromHex(input.value) },
      };
      commitTeam();
    });
  }

  for (const input of overlay.querySelectorAll<HTMLInputElement>(".team-name-input")) {
    input.addEventListener("input", () => {
      const value = input.value.slice(0, 16);
      team = input.dataset.team === "away" ? { ...team, awayName: value } : { ...team, homeName: value };
      commitTeam({ rebuild: false });
    });
  }

  /** Pinta las dos camisetas del escaparate con lo elegido ahora mismo. */
  function syncJerseys(): void {
    const kits: Record<"home" | "away", Kit> = { home: homeKitOf(team), away: awayKitOf(team) };
    // El rival lleva un 9 fijo: es un maniqui para ver el contraste, no un
    // jugador — y darle un numero editable invitaria a configurarlo.
    const numbers: Record<"home" | "away", number> = { home: shirtNumber, away: 9 };
    for (const side of ["home", "away"] as const) {
      const jersey = overlay.querySelector<HTMLElement>(`[data-jersey="${side}"]`);
      if (jersey) {
        jersey.style.cssText = kitPreviewStyle(kits[side]);
        // El escaparate tambien es la FICHA del campo, no una camiseta.
        jersey.className = `kit-jersey kit-token kit-token--${kits[side].pattern}`;
        const num = jersey.querySelector<HTMLElement>(".kit-jersey-num");
        if (num) num.textContent = String(numbers[side]);
      }
      const label = overlay.querySelector<HTMLElement>(`[data-stage-kit="${side}"]`);
      if (label) label.textContent = t(kits[side].label);
    }
  }

  /**
   * La ficha del apartado del dorsal: tu equipacion con tu numero.
   *
   * Se pinta con la equipacion de CASA siempre, aunque estes mirando la del
   * rival en la otra pestaña: aqui se esta eligiendo TU dorsal, y verlo sobre
   * la camiseta del contrario no diria nada.
   */
  function syncShirtToken(): void {
    const token = overlay.querySelector<HTMLElement>("[data-shirt-token]");
    const num = overlay.querySelector<HTMLElement>("[data-shirt-token-num]");
    if (!token) return;
    const kit = homeKitOf(team);
    token.style.cssText = kitPreviewStyle(kit);
    token.className = `shirt-token kit-token kit-token--${kit.pattern}`;
    if (num) num.textContent = String(shirtNumber);
  }

  const syncTeamUi = (rebuild: boolean): void => {
    // Las miniaturas de "A medida" y "Automática" dependen de lo elegido, asi
    // que la rejilla se reconstruye salvo cuando solo se ha tecleado un nombre
    // (reconstruirla ahi le robaria el foco al input a cada letra).
    if (rebuild) buildKitGrid();
    syncShirtToken();

    for (const tab of overlay.querySelectorAll<HTMLElement>(".kit-side")) {
      const on = tab.dataset.side === kitSide;
      tab.classList.toggle("is-active", on);
      tab.setAttribute("aria-selected", on ? "true" : "false");
    }

    const chosen = kitSide === "home" ? team.homeKitId : team.awayKitId;
    for (const btn of kitGrid.querySelectorAll<HTMLElement>(".kit-card")) {
      const on = btn.dataset.kit === chosen;
      btn.classList.toggle("is-selected", on);
      btn.setAttribute("aria-checked", on ? "true" : "false");
    }

    // Los selectores de color solo tienen sentido sobre TU camiseta.
    const isCustom = kitSide === "home" && team.homeKitId === CUSTOM_KIT_ID;
    kitCustom.hidden = !isCustom;
    for (const btn of kitPatterns.querySelectorAll<HTMLElement>(".kit-pattern-pill")) {
      const on = btn.dataset.pattern === team.custom.pattern;
      btn.classList.toggle("is-selected", on);
      btn.setAttribute("aria-checked", on ? "true" : "false");
    }
    for (const input of overlay.querySelectorAll<HTMLInputElement>("[data-kit-color]")) {
      const key = input.dataset.kitColor as "primary" | "secondary" | "accent";
      input.value = hex(team.custom[key]);
    }

    syncJerseys();
    kitBlurb.textContent =
      team.awayKitId === AUTO_AWAY_KIT
        ? t("El rival cambia de camiseta solo para no confundirse con la tuya.")
        : t("Equipación del rival fijada a mano.");
  };

  function commitTeam({ rebuild = true }: { rebuild?: boolean } = {}): void {
    saveStoredTeamSetup(team);
    applyTeamSetup(team);
    syncTeamUi(rebuild);
  }

  for (const input of overlay.querySelectorAll<HTMLInputElement>(".team-name-input")) {
    input.value = input.dataset.team === "away" ? team.awayName : team.homeName;
  }

  // ----------------------------------------------------------------- cancha

  const setPitch = (id: PitchSkinId): void => {
    pitchSkin = id;
    saveStoredPitchSkin(id);
    pitchBlurb.textContent = t(PITCH_SKINS[id].tagline);
    for (const btn of pitchStrip.querySelectorAll<HTMLElement>(".pitch-card")) {
      const on = btn.dataset.pitch === id;
      btn.classList.toggle("is-selected", on);
      btn.setAttribute("aria-checked", on ? "true" : "false");
    }
  };

  for (const id of PITCH_SKIN_IDS) {
    const skin = PITCH_SKINS[id];
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `pitch-card pitch-card--${id}`;
    btn.dataset.pitch = id;
    btn.setAttribute("role", "radio");
    btn.style.cssText = pitchPreviewStyle(id);
    btn.innerHTML = `
      <span class="pitch-card-swatch" aria-hidden="true"></span>
      <span class="pitch-card-label">${t(skin.label)}</span>
    `;
    btn.addEventListener("click", () => setPitch(id));
    pitchStrip.appendChild(btn);
  }

  /*
   * La ASISTENCIA ya no se elige.
   *
   * Era una pestaña entera para un ajuste que casi nadie tocaba y que, mal
   * puesto, empeora el juego sin que quien lo puso sepa por que. Se queda en
   * "normal" para todo el mundo — sigue guardada y respetada si alguien la
   * cambio en su dia, pero no se ofrece.
   */

  // -------------------------------------------------------------- duracion

  const minutesInput = q<HTMLInputElement>(".minutes-input");

  /**
   * Fija la duracion y la deja aplicada YA.
   *
   * Se aplica al vuelo (no solo al cerrar) porque `MATCH.DURATION` es lo que
   * lee la simulacion al arrancar: si se guardara solo el numero y se aplicara
   * en otro sitio, habria dos fuentes de verdad para lo mismo.
   */
  /** Vuelca el formato entero al juego y refresca los botones. */
  const syncFormat = (): void => {
    setMatchFormat({ minutes, periods, onDraw });
    minutesInput.value = String(minutes);
    for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-min]")) {
      btn.classList.toggle("is-selected", Number(btn.dataset.min) === minutes);
    }
    for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-periods]")) {
      btn.classList.toggle("is-selected", Number(btn.dataset.periods) === periods);
    }
    for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-draw]")) {
      btn.classList.toggle("is-selected", btn.dataset.draw === onDraw);
    }
    const hint = overlay.querySelector("[data-total-hint]");
    if (hint) {
      // El total es lo que la gente quiere saber: "2 x 8" no dice cuanto dura.
      hint.textContent =
        periods > 1
          ? t("{p} x {m} min · {total} min de juego", { p: periods, m: minutes, total: periods * minutes })
          : "";
    }
  };

  const setMinutes = (n: number): void => {
    minutes = Math.min(MATCH_MINUTES_MAX, Math.max(MATCH_MINUTES_MIN, Math.round(n)));
    save(MINUTES_STORAGE_KEY, String(minutes));
    syncFormat();
  };

  minutesInput.addEventListener("input", () => {
    const n = Number(minutesInput.value);
    if (Number.isFinite(n) && n > 0) setMinutes(n);
  });
  minutesInput.addEventListener("blur", () => setMinutes(minutes));

  for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-min-dir]")) {
    btn.addEventListener("click", () => setMinutes(minutes + Number(btn.dataset.minDir)));
  }
  for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-min]")) {
    btn.addEventListener("click", () => setMinutes(Number(btn.dataset.min)));
  }
  for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-periods]")) {
    btn.addEventListener("click", () => {
      periods = Number(btn.dataset.periods);
      save(PERIODS_STORAGE_KEY, String(periods));
      syncFormat();
    });
  }
  for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-draw]")) {
    btn.addEventListener("click", () => {
      onDraw = btn.dataset.draw === "draw" ? "draw" : "golden";
      save(DRAW_STORAGE_KEY, onDraw);
      syncFormat();
    });
  }

  // ------------------------------------------------------------------- plan

  const SLIDERS: ReadonlyArray<{
    key: keyof TeamPlan;
    label: string;
    min: number;
    max: number;
    low: string;
    high: string;
  }> = [
    { key: "mentality", label: "Mentalidad", min: -1, max: 1, low: "Replegado", high: "Volcado" },
    { key: "pressing", label: "Presion", min: 0, max: 1, low: "Esperar", high: "Asfixiar" },
    { key: "width", label: "Amplitud", min: 0, max: 1, low: "Estrecho", high: "Abierto" },
    { key: "tempo", label: "Ritmo", min: 0, max: 1, low: "Conducir", high: "Tocar" },
  ];

  const syncPlanUi = (): void => {
    for (const s of SLIDERS) {
      const input = planSliders.querySelector<HTMLInputElement>(`input[data-key="${s.key}"]`);
      if (input) input.value = String(plan[s.key]);
    }
    const match = PLAN_PRESETS.find((p) =>
      SLIDERS.every((s) => Math.abs(p.plan[s.key] - plan[s.key]) < 1e-6),
    );
    planBlurb.textContent = match ? match.blurb : t("Plan a medida.");
    for (const btn of planPresets.querySelectorAll<HTMLElement>(".plan-pill")) {
      const on = btn.dataset.preset === match?.id;
      btn.classList.toggle("is-selected", on);
      btn.setAttribute("aria-checked", on ? "true" : "false");
    }
  };

  const commitPlan = (): void => {
    save(PLAN_STORAGE_KEY, JSON.stringify(plan));
    applyTeamPlan(plan);
    syncPlanUi();
  };

  for (const preset of PLAN_PRESETS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "plan-pill";
    btn.dataset.preset = preset.id;
    btn.setAttribute("role", "radio");
    btn.textContent = t(preset.label);
    btn.addEventListener("click", () => {
      plan = { ...preset.plan };
      commitPlan();
    });
    planPresets.appendChild(btn);
  }

  for (const s of SLIDERS) {
    const row = document.createElement("label");
    row.className = "plan-slider";
    row.innerHTML = `
      <span class="plan-slider-name">${t(s.label)}</span>
      <input type="range" data-key="${s.key}" min="${s.min}" max="${s.max}" step="0.05" aria-label="${t(s.label)}" />
      <span class="plan-slider-ends"><em>${t(s.low)}</em><em>${t(s.high)}</em></span>
    `;
    const input = row.querySelector("input") as HTMLInputElement;
    input.addEventListener("input", () => {
      plan = { ...plan, [s.key]: Number(input.value) };
      commitPlan();
    });
    planSliders.appendChild(row);
  }

  // ------------------------------------------------------------------ marco

  const setTab = (name: string): void => {
    for (const tab of overlay.querySelectorAll<HTMLElement>(".customize-tab")) {
      tab.classList.toggle("is-active", tab.dataset.tab === name);
    }
    for (const pane of overlay.querySelectorAll<HTMLElement>(".customize-pane")) {
      pane.classList.toggle("is-active", pane.dataset.pane === name);
    }
  };

  for (const tab of overlay.querySelectorAll<HTMLElement>(".customize-tab")) {
    tab.addEventListener("click", () => setTab(tab.dataset.tab ?? t("jugador")));
  }

  const settings = (): CustomizeSettings => ({
    shirtNumber,
    nickname,
    minutes,
    periods,
    onDraw,
    assist,
    pitchSkin,
    plan: { ...plan },
    team: { ...team, custom: { ...team.custom } },
  });

  const close = (): void => {
    overlay.classList.add("is-hidden");
    resolveOpen?.(settings());
    resolveOpen = null;
  };

  for (const btn of overlay.querySelectorAll<HTMLElement>("[data-close]")) {
    btn.addEventListener("click", close);
  }

  q("[data-reset]").addEventListener("click", () => {
    shirtNumber = DEFAULT_SHIRT;
    // El apodo NO se restablece: es tu cuenta, no un ajuste del partido.
    assist = DEFAULT_ASSIST;
    pitchSkin = PITCH_SKIN_IDS[0]!;
    plan = { ...DEFAULT_PLAN };
    team = { ...DEFAULT_TEAM_SETUP, custom: { ...DEFAULT_TEAM_SETUP.custom } };
    for (const input of overlay.querySelectorAll<HTMLInputElement>(".team-name-input")) {
      input.value = "";
    }
    commitTeam();
    save(SHIRT_STORAGE_KEY, String(shirtNumber));
    save(ASSIST_STORAGE_KEY, assist);
    saveStoredPitchSkin(pitchSkin);
    minutes = Math.round(MATCH_DEFAULT_SECONDS / 60);
    syncShirt();
    syncFormat();
    setPitch(pitchSkin);
    commitPlan();
  });

  const onKey = (event: KeyboardEvent): void => {
    if (overlay.classList.contains("is-hidden")) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };
  window.addEventListener("keydown", onKey, true);

  // Estado inicial coherente con lo guardado.
  commitTeam();
  showNick();
  syncShirt();
  syncFormat();
  setPitch(pitchSkin);
  commitPlan();

  return {
    open(): Promise<CustomizeSettings> {
      setTab("jugador");
      overlay.classList.remove("is-hidden");
      q<HTMLButtonElement>(".customize-close").focus();
      return new Promise((resolve) => {
        resolveOpen = resolve;
      });
    },
    current: settings,
    dispose(): void {
      window.removeEventListener("keydown", onKey, true);
      unsubNick();
      unsubUnlocks();
      overlay.remove();
    },
  };
}
